import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { entitiesService } from "@/services/entities.service";
import { ROUTING_CLASS_TO_GROUP } from "@/constants/entityGroups";
import { extractEventDates, getRelativeDateWithTime } from "@/utils/eventDateUtils";
import type { CollectionEntityEntry } from "@/utils/entityMappers";

/**
 * Fetch entities for multiple stash IDs in a single batch request,
 * then deduplicate into CollectionEntityEntry[].
 */
export function useBatchStashEntities(stashIds: string[], enabled: boolean) {
  const stashIdsKey = [...stashIds].sort().join(",");

  const { data: batchEntitiesData, isLoading, isFetching } = useQuery({
    queryKey: ["stash-entities-batch", stashIdsKey],
    enabled: enabled && stashIds.length > 0,
    staleTime: 5 * 60_000,
    queryFn: () => entitiesService.getEntitiesForMessageBatch(stashIds),
  });

  const entities = useMemo<CollectionEntityEntry[]>(() => {
    if (!batchEntitiesData?.results) return [];
    const seen = new Set<string>();
    const entries: CollectionEntityEntry[] = [];
    for (const entityList of Object.values(batchEntitiesData.results)) {
      for (const entity of entityList) {
        const entityId = entity.entity_id || entity.id;
        const name = entity.name || entity.entity_name;
        if (!entityId || !name || seen.has(entityId)) continue;
        seen.add(entityId);
        const group = ROUTING_CLASS_TO_GROUP[entity.routing_class ?? ""] ?? "other";
        let subtitle: string | undefined;
        if (group === "events") {
          const dates = extractEventDates(entity);
          // Skip past events
          if (dates.start && dates.start < new Date()) continue;
          if (dates.start) subtitle = getRelativeDateWithTime(dates.start, dates.allDay);
        } else if (group === "places") {
          const gp = entity.google_places || entity.enrichment_data?.google_places;
          const raw = gp?.primary_type || entity.category || (Array.isArray(gp?.types) ? gp.types[0] : undefined);
          if (raw) subtitle = raw.replace(/_/g, " ").replace(/([A-Z])/g, " $1").trim().replace(/\b\w/g, (c: string) => c.toUpperCase());
        }
        entries.push({ entity_id: entityId, name, group, subtitle });
      }
    }
    return entries;
  }, [batchEntitiesData]);

  return {
    entities,
    hasEntities: entities.length > 0,
    isLoading,
    isFetching,
  };
}
